import type { ButtonHTMLAttributes } from 'react';
import React, { forwardRef, useState } from 'react';

import type { USER_ROLE_WITHOUT_GUEST_AND_USER } from 'cpool-constants';

import useUserInfo from '../../hooks/useUserInfo';
import Loading from '../Loading';
import type { REPUDIATE_ROLES_ENUM, ROLES_ENUM } from '~/constants.ts';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';

export type PermissionRole = (typeof ROLES_ENUM)[number] | (typeof REPUDIATE_ROLES_ENUM)[number];
export type PropRoles = ReadonlyArray<PermissionRole | ReadonlyArray<PermissionRole>> | PermissionRole;

export interface TreatButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick?: (...args: any[]) => Promise<void> | void;
    children: React.ReactNode;
    className?: string;
    hideWhileLoading?: boolean;
    loadingFallback?: React.ReactNode;
    roles?: PropRoles;
    disabled?: boolean;
    message?: string;
}

const checkRole = (userRole: USER_ROLE_WITHOUT_GUEST_AND_USER, role: PermissionRole) => {
    const parsedPermissionRole: string = role;

    if (parsedPermissionRole.charAt(0) === '!') return userRole !== parsedPermissionRole.substring(1);
    return userRole === parsedPermissionRole;
};

const TreatButton = forwardRef<HTMLButtonElement, TreatButtonProps>(
    (
        {
            children,
            className,
            disabled,
            hideWhileLoading = false,
            loadingFallback = <Loading />,
            message,
            onClick,
            roles = [],
            ...props
        },
        ref: React.LegacyRef<HTMLButtonElement> | undefined,
    ) => {
        const [loading, setLoading] = useState(false);

        const userRole = useUserInfo()?.role;

        const callback = useCallbackWithErrorHandler(
            async (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                if (!onClick) return;

                setLoading(true);
                await onClick(evt);
                setLoading(false);
            },
            [onClick],
            message,
        );

        const mappedRoles = Array.isArray(roles) ? roles : [roles];

        const hasPermissions =
            !mappedRoles.length ||
            (userRole !== undefined &&
                mappedRoles.some((role) => {
                    if (Array.isArray(role)) return role.every((entry) => checkRole(userRole, entry));
                    return checkRole(userRole, role);
                }));

        if (!hasPermissions) return null;

        return (
            <>
                {loading && loadingFallback}
                {!(loading && hideWhileLoading) && (
                    <button
                        disabled={loading || disabled}
                        onClick={callback}
                        className={className}
                        {...props}
                        ref={ref}
                    >
                        {children}
                    </button>
                )}
            </>
        );
    },
);

export default TreatButton;
